import React, { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import 'swiper/css';
import Ambassador_Details from '../../api/ambassador';
import Head from 'next/head';
import Steps from '../../components/Ambassador/steps';
import TravelImages from '../../components/Ambassador/travelImages';
import KeyBenefits from '../../components/Ambassador/keyBenefits';
import client from '../../apollo-client';
interface AmbassadorData {
  ambassadorPage: {
    banner_title: string;
    canonical: string;
    hero_image: {
      alternativeText: string;
      url: string;
    };
    intro_description: string;
    intro_header: string;
    intro_image: {
      alternativeText: string;
      url: string;
    };
    intro_subheader: string;
    keyBenefits: Array<{
      description: string;
      id: string;
      image: Array<{
        alternativeText: string;
        url: string;
      }>;
      title: string;
    }>;
    meta_description: string;
    meta_title: string;
    page_title: string;
    program_description: string;
    program_image: Array<{
      alternativeText: string;
      url: string;
    }>;
    program_title: string;
    published_at: string;
    travelImages: Array<{
      id: string;
      Image: Array<{
        alternativeText: string;
        url: string;
      }>;
    }>;
  };
}
interface AmbassadorPageProps {
  data: AmbassadorData;
}
const AmbassadorPage: React.FC<AmbassadorPageProps> = ({ data }) => {
  const router = useRouter();
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});

  const handleImageLoad = (id: string) => {
    setImagesLoaded(prev => ({
      ...prev,
      [id]: true
    }));
  };

  return (
    <>
      <Head>
        <title>{data?.ambassadorPage?.meta_title}</title>
        <meta
          name="description"
          content={data?.ambassadorPage?.meta_description}
        />
        <link rel="canonical" href={data?.ambassadorPage?.canonical} />
      </Head>
      <div className="px-4 py-4">
        <div className="relative w-full">
          <div
            className={`bg-[#EFEFEF] w-full h-[180px] lg:h-[600px] ${
              imagesLoaded['hero'] ? 'hidden' : 'animate-pulse'
            }`}
          ></div>
          <div className={imagesLoaded['hero'] ? '' : 'invisible'}>
            <Image
              alt="Ambassador Image"
              src={data?.ambassadorPage?.hero_image?.url}
              width={1920}
              height={1080}
              className="w-full xl:h-[600px] lg:h-[380px] h-[180px] md:h-[300px] object-cover object-center"
              priority
              onLoad={() => handleImageLoad('hero')}
            />
          </div>
          <div className="absolute text-white font-bold inset-0 flex items-start pt-[18%] sm:pt-[15%] md:pt-[8%]">
            <div className="ml-[3%] md:ml-[8%] lg:ml-[12%] xl:ml-[14%] w-[90%] md:w-[60%] lg:w-[45%] xl:w-[650px]">
              <h1 className="text-[21px] md:text-[40px] xl:text-[75px] leading-5 md:leading-[40px] xl:leading-[90px] text-left">
                {data?.ambassadorPage?.banner_title}
              </h1>
            </div>
          </div>
          <div
            className="absolute -bottom-[35px] sm:-bottom-[42px] md:-bottom-15 xl:-bottom-20 right-0 md:right-[2rem] xl:right-[18rem] xl:w-[108px] xl:h-[108px] w-[35px] h-[35px] sm:w-[42px] sm:h-[42px] md:w-[50px] md:h-[50px] bg-[#FF4D4D] mb-4 mr-4"
            style={{ clipPath: 'polygon(0% 0%, 100% 0%, 0% 100%)' }}
          />
        </div>

        <div className="flex flex-col md:flex-row gap-8 px-4 md:px-[5%] xl:px-[15%] py-[4%]">
          <div className="flex-1 space-y-4 lg:space-y-6">
            <div className="flex items-start gap-2 mt-4 lg:-ml-6">
              <div
                className="hidden lg:block xl:w-[29px] xl:h-[29px] xl:mt-2 md:mt-0 mt-[2px]"
                style={{
                  width: '29px',
                  height: '29px',
                  backgroundColor: '#FF4D4D',
                  clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)'
                }}
              />
              <h1 className="text-[20px] md:text-[20px] xl:text-[40px] font-bold text-primary leading-tight">
                {data?.ambassadorPage?.intro_header}
              </h1>
            </div>

            <h2 className="text-[14px] md:text-[15px] xl:text-[24px] font-bold text-primary">
              {data?.ambassadorPage?.intro_subheader}
            </h2>

            <div className="xl:space-y-4 text-[14px] md:text-[14px] xl:text-lg text-primary">
              <div
                className="lg:block hidden"
                dangerouslySetInnerHTML={{
                  __html: data?.ambassadorPage?.intro_description.replace(
                    /\n/g,
                    '<br />'
                  )
                }}
              ></div>

              <div className="lg:hidden block">
                <p>{data?.ambassadorPage?.intro_description}</p>
              </div>
            </div>
          </div>

          <div className="hidden sm:hidden md:block flex-shrink-0 mt-4 mx-auto md:mx-0">
            <div className="relative">
              <div
                className={`bg-gray-200 rounded-lg md:w-[311px] md:h-[418px] lg:w-[325px] lg:h-[470px] ${
                  imagesLoaded['ambassador1'] ? 'hidden' : 'animate-pulse'
                }`}
              ></div>
              <div className={imagesLoaded['ambassador1'] ? '' : 'invisible'}>
                <Image
                  src={data?.ambassadorPage?.intro_image?.url}
                  alt="Person taking photo of tropical islands with phone"
                  className="rounded-lg object-cover md:w-[311px] md:h-[418px] lg:w-[325px] lg:h-[470px]"
                  loading="lazy"
                  width={325}
                  height={510}
                  onLoad={() => handleImageLoad('ambassador1')}
                />
              </div>
            </div>
          </div>
        </div>

        <KeyBenefits
          benefits={data?.ambassadorPage?.keyBenefits.map(benefit => ({
            ...benefit,
            id: parseInt(benefit.id, 10)
          }))}
        />

        <div className="flex flex-col md:flex-row px-4 md:px-[5%] xl:px-[15%] py-4 gap-12 items-center">
          <div className="flex-1 space-y-6">
            <div className="hidden md:flex items-start -ml-2">
              <div
                className="hidden lg:block lg:-ml-6"
                style={{ marginTop: '8px', flexShrink: 0 }}
              >
                <div
                  style={{
                    width: '21px',
                    height: '29px',
                    backgroundColor: '#FF4D4D',
                    clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)'
                  }}
                />
              </div>
              <div className="relative pl-2">
                <h2 className="text-[20px] md:text-[24px] xl:text-[40px] lg:text-[30px] font-bold text-[#1E2875] leading-tight">
                  {data?.ambassadorPage?.program_title}
                </h2>
              </div>
            </div>

            <div className="block md:hidden relative items-center">
              <div className="flex items-center">
                <h2 className="text-[20px] md:text-[24px] xl:text-[40px] font-bold text-[#1E2875] leading-tight">
                  See Sight tours
                  <span
                    className="inline-block align-middle relative"
                    style={{
                      width: '14px',
                      height: '16px',
                      backgroundColor: '#FF4D4D',
                      clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)',
                      transform: 'rotate(-90deg)',
                      bottom: '7px'
                    }}
                  />
                </h2>
              </div>
              <div className="absolute top-full text-[#1E2875] text-[20px] md:text-[24px] lg:text-[40px] font-bold leading-tight whitespace-nowrap">
                Partnership Program
              </div>
            </div>

            <div className="space-y-4 text-[14px] md:text-[14px] xl:text-[18px] text-[#1E2875] py-4">
              <p>{data?.ambassadorPage?.program_description}</p>
            </div>

            <button
              onClick={() => router.push('/ambassador/BoardingForm')}
              className="w-full md:w-auto bg-[#1E2875] text-white px-12 py-3 rounded font-bold text-lg hover:bg-primary transition-colors"
            >
              APPLY
            </button>
          </div>

          <div className="flex-1 relative">
            <div
              className={`bg-gray-200 w-full h-[557px] ${
                imagesLoaded['ambassador5'] ? 'hidden' : 'animate-pulse'
              }`}
            ></div>
            <div className={imagesLoaded['ambassador5'] ? '' : 'invisible'}>
              <Image
                alt="Ambassadors Taking Selfie"
                src={data?.ambassadorPage?.program_image[0]?.url}
                width={592}
                height={557}
                loading="lazy"
                className="w-full"
                onLoad={() => handleImageLoad('ambassador5')}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center lg:pb-16 px-4 mt-5">
          <h2 className="text-[22px] md:text-[24px] lg:text-4xl font-bold text-[#1E2875] mb-8 text-center">
            Start your next adventure with us!
          </h2>
          <div className="relative">
            <div
              className={`bg-gray-200 w-[200px] h-[200px] ${
                imagesLoaded['logo'] ? 'hidden' : 'animate-pulse'
              }`}
            ></div>
            <div className={imagesLoaded['logo'] ? '' : 'invisible'}>
              <Image
                alt="SeeSight Tours Logo"
                src="/navbar-seesight-logo.png"
                className="w-full xl:max-w-[548px]"
                loading="lazy"
                width={200}
                height={200}
                onLoad={() => handleImageLoad('logo')}
              />
            </div>
          </div>
        </div>

        <TravelImages travelImages={data?.ambassadorPage?.travelImages} />

        <Steps />
        <div className="flex justify-center mb-3">
          <button
            onClick={() => router.push('/ambassador/BoardingForm')}
            className="bg-[#1E2959] text-white px-8 md:px-14 py-4 lg:py-5 rounded-lg font-semibold text-xl hover:bg-[#161d40] transition-colors md:w-3/4 lg:w-1/3"
          >
            Apply Now
          </button>
        </div>
      </div>
    </>
  );
};

export async function getStaticProps() {
  const { data } = await client.query({
    query: Ambassador_Details
  });

  return {
    props: {
      data
    }
  };
}

export default AmbassadorPage;
